import type { CSSProperties, ReactNode } from "react";
import {
  Badge,
  Button,
  Cluster,
  EmptyState,
  Kicker,
  Note,
  Panel,
  Stack,
  Status,
  Table,
  Title,
} from "@fwf/fwfa-design-system";

import { applyFilters, toFilterable } from "../filters";
import type { FilterState } from "../filters";
import { formatIsoDate, titleCase } from "../format";
import type { Actions, AdminData, Toasts } from "../hooks";
import { lambdaState } from "../state";
import { stateTone } from "../types";
import type { AdminLambdaMicrovm } from "../types";

export type SectionProps = {
  data: AdminData;
  filters: FilterState;
  actions: Actions;
  toasts: Toasts;
};

type LambdaAction = "resume" | "suspend" | "terminate";

/**
 * Toast wording per action. Every one of these is a request AWS acts on
 * asynchronously, so the copy promises the request, not the finished state.
 */
const ACTION_COPY: Record<LambdaAction, { success: string; failure: string }> = {
  resume: { success: "MicroVM resuming", failure: "Could not resume the MicroVM" },
  suspend: { success: "MicroVM stopping", failure: "Could not stop the MicroVM" },
  terminate: { success: "Termination requested", failure: "Could not terminate the MicroVM" },
};

/** Compact buttons read as one control group at the tighter cluster gap. */
const ACTION_GAP = { "--fwfa-cluster-gap": "var(--fwfa-space-2)" } as CSSProperties;

function SectionHeading({ children }: { children?: ReactNode }) {
  return (
    <div className="fwfa-section-header">
      <div>
        <Kicker>Workspace provider</Kicker>
        <Title as="h2" className="fwfa-title--section">
          AWS Lambda MicroVMs
        </Title>
      </div>
      {children}
    </div>
  );
}

export function LambdaSection({ data, filters, actions }: SectionProps) {
  const lambda = data.lambda;
  const notice = data.notices.lambda;

  // Absent and quiet means AWS is simply not part of this deployment, so it
  // should not take up space on the page at all.
  if (!lambda && !notice) return null;

  if (!lambda) {
    return (
      <Panel>
        <Stack>
          <SectionHeading />
          <Note variant="warn">{notice}</Note>
        </Stack>
      </Panel>
    );
  }

  const runAction = (microvm: AdminLambdaMicrovm, action: LambdaAction) => {
    void actions.run({
      key: `lambda:${microvm.microvmId}:${action}`,
      url: `/api/admin/lambda-microvms/${action}`,
      body: { microvmId: microvm.microvmId },
      // Resume and suspend are reversible in one click, so they no longer
      // interrupt. Terminate destroys the workspace, so it keeps a confirm.
      confirm:
        action === "terminate"
          ? {
              title: "Terminate this MicroVM?",
              body: `${microvm.microvmId} and its workspace will be permanently deleted. It cannot be restarted afterwards.`,
              confirmLabel: "Terminate MicroVM",
              danger: true,
            }
          : undefined,
      successTitle: ACTION_COPY[action].success,
      successMessage: microvm.microvmId,
      failureTitle: ACTION_COPY[action].failure,
    });
  };

  const microvms = applyFilters(lambda.microvms, filters, (microvm) =>
    toFilterable(lambdaState(microvm), "n/a", [
      microvm.microvmId,
      microvm.state,
      microvm.imageVersion,
    ]),
  );

  return (
    <Panel>
      <Stack>
        <SectionHeading>
          {/* The image ARN is long enough to blow out a fixed column, so it
              lives in the header meta and is allowed to wrap. */}
          <div className="min-w-0 max-w-full">
            <p className="fwfa-subtitle fwfa-subtitle--sm">
              {lambda.region} · configured FreshVibe image only
            </p>
            <p className="fwfa-subtitle fwfa-subtitle--sm font-mono break-all">{lambda.imageArn}</p>
          </div>
        </SectionHeading>

        {lambda.microvms.length === 0 && (
          <EmptyState title="No MicroVMs for the configured image">
            Nothing is running against this image ARN in {lambda.region}.
          </EmptyState>
        )}

        {lambda.microvms.length > 0 && microvms.length === 0 && (
          <EmptyState title="No MicroVMs match these filters">
            Clear the search or the state filter to see the full list.
          </EmptyState>
        )}

        {microvms.length > 0 && (
          <Table reflow>
            <thead>
              <tr>
                <th scope="col">MicroVM</th>
                <th scope="col">State</th>
                <th scope="col">Image version</th>
                <th scope="col">Started</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {microvms.map((microvm) => {
                const state = lambdaState(microvm);
                // TERMINATING is still winding down; only TERMINATED is the end
                // of the line, and only that state earns the dead-end note.
                const isTerminated = microvm.state.toUpperCase() === "TERMINATED";
                const busy = (action: LambdaAction) =>
                  actions.isPending(`lambda:${microvm.microvmId}:${action}`);

                return (
                  <tr key={microvm.microvmId}>
                    <td data-label="MicroVM">
                      <span className="font-mono text-ink">{microvm.microvmId}</span>
                    </td>

                    <td data-label="State">
                      <Status tone={stateTone(state)} live={state === "running"}>
                        {titleCase(microvm.state)}
                      </Status>
                    </td>

                    <td data-label="Image version">
                      <Badge variant="soft">{microvm.imageVersion}</Badge>
                    </td>

                    <td data-label="Started">{formatIsoDate(microvm.startedAt)}</td>

                    <td data-label="Actions">
                      <Cluster style={ACTION_GAP}>
                        {state === "stopped" && (
                          <Button
                            variant="secondary"
                            compact
                            loading={busy("resume")}
                            disabled={busy("resume")}
                            onClick={() => runAction(microvm, "resume")}
                          >
                            Resume
                          </Button>
                        )}
                        {state === "running" && (
                          <Button
                            variant="secondary"
                            compact
                            loading={busy("suspend")}
                            disabled={busy("suspend")}
                            onClick={() => runAction(microvm, "suspend")}
                          >
                            Stop
                          </Button>
                        )}
                        {state !== "terminal" && (
                          <Button
                            variant="secondary"
                            compact
                            loading={busy("terminate")}
                            disabled={busy("terminate")}
                            onClick={() => runAction(microvm, "terminate")}
                          >
                            Terminate
                          </Button>
                        )}
                        {isTerminated && (
                          <span className="fwfa-subtitle fwfa-subtitle--sm">
                            Cannot be restarted, create a new workspace instead.
                          </span>
                        )}
                      </Cluster>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Stack>
    </Panel>
  );
}
